import React, { useEffect, useState, useCallback } from "react";
import { Mail, CheckCircle2, RefreshCw, ShieldCheck, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

/**
 * Admin tab — Pending email verifications.
 *
 * Lists every password-registered user who hasn't confirmed their email yet
 * and exposes:
 *   • One-click "Force verify" (calls POST /api/admin/users/{uid}/force-verify)
 *   • One-click "Resend confirmation" (calls POST /api/auth/resend-verification)
 *
 * Useful when Resend bounces, the inbox is unreachable, or the user lost the
 * link and you need to unblock them on a support call.
 */
export default function PendingVerificationsTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState({}); // { user_id: "verify" | "resend" }
  const [delivery, setDelivery] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: rowsData }, { data: deliveryData }] = await Promise.all([
        api.get("/admin/pending-verifications"),
        api.get("/admin/email-delivery-status").catch(() => ({ data: null })),
      ]);
      setRows(Array.isArray(rowsData) ? rowsData : []);
      if (deliveryData) setDelivery(deliveryData);
    } catch (e) {
      toast.error("Couldn't load pending verifications");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const forceVerify = async (u) => {
    setWorking((w) => ({ ...w, [u.user_id]: "verify" }));
    try {
      await api.post(`/admin/users/${u.user_id}/force-verify`);
      toast.success(`${u.email} marked as verified`);
      setRows((r) => r.filter((x) => x.user_id !== u.user_id));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Force-verify failed");
    } finally {
      setWorking((w) => { const n = { ...w }; delete n[u.user_id]; return n; });
    }
  };

  const resend = async (u) => {
    setWorking((w) => ({ ...w, [u.user_id]: "resend" }));
    try {
      const { data } = await api.post("/auth/resend-verification", { email: u.email });
      if (data?.rate_limited) {
        toast("We just sent one — wait 60 seconds before re-firing.", { duration: 4500 });
      } else {
        toast.success(`Confirmation re-sent to ${u.email}`);
      }
    } catch {
      toast.error("Couldn't resend right now");
    } finally {
      setWorking((w) => { const n = { ...w }; delete n[u.user_id]; return n; });
    }
  };

  return (
    <div data-testid="pending-verifications-tab" className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display font-black text-2xl text-white tracking-tight">Pending email verifications</h2>
          <p className="text-white/55 text-sm mt-1">
            Users who registered with a password but haven't clicked the confirmation link yet. Force-verify if delivery is failing.
          </p>
        </div>
        <button
          data-testid="pending-verifications-reload"
          onClick={load}
          className="bg-white/10 hover:bg-white/15 text-white text-xs font-bold px-3 py-2 rounded-full inline-flex items-center gap-2"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>

      {delivery && (
        <SenderConfigCard delivery={delivery} onSaved={(d) => setDelivery(d)} />
      )}

      {!loading && rows.length === 0 && (
        <div data-testid="pending-verifications-empty" className="bg-emerald-500/10 border border-emerald-500/40 rounded-xl px-5 py-6 text-emerald-100 flex items-center gap-3">
          <CheckCircle2 className="w-5 h-5 text-emerald-300" />
          <div>
            <div className="font-bold">All clear.</div>
            <div className="text-emerald-200/80 text-sm">Nobody is currently waiting to confirm an email.</div>
          </div>
        </div>
      )}

      {rows.length > 0 && (
        <div data-testid="pending-verifications-list" className="rounded-xl border border-white/10 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-white/5 text-white/60 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left font-bold px-4 py-2.5">User</th>
                <th className="text-left font-bold px-4 py-2.5">Registered</th>
                <th className="text-left font-bold px-4 py-2.5">Last token</th>
                <th className="text-right font-bold px-4 py-2.5">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {rows.map((u) => {
                const busy = working[u.user_id];
                return (
                  <tr key={u.user_id} data-testid={`pending-row-${u.user_id}`} className="hover:bg-white/[0.03]">
                    <td className="px-4 py-3">
                      <div className="font-semibold text-white">{u.name || "Unnamed user"}</div>
                      <div className="text-white/55 text-xs flex items-center gap-1.5">
                        <Mail className="w-3 h-3" /> {u.email}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-white/70 text-xs tabular-nums">
                      {u.created_at ? new Date(u.created_at).toLocaleString() : "—"}
                    </td>
                    <td className="px-4 py-3 text-white/70 text-xs tabular-nums">
                      {u.last_token_issued_at ? (
                        <div>
                          <div>{new Date(u.last_token_issued_at).toLocaleString()}</div>
                          {u.last_token_expires_at && (() => {
                            const exp = new Date(u.last_token_expires_at);
                            const expired = exp.getTime() < Date.now();
                            return (
                              <div className={`text-[10px] uppercase font-bold ${expired ? "text-red-300" : "text-amber-300"}`}>
                                {expired ? "Expired" : `Expires ${exp.toLocaleString()}`}
                              </div>
                            );
                          })()}
                        </div>
                      ) : (
                        <span className="text-red-300 inline-flex items-center gap-1">
                          <AlertTriangle className="w-3 h-3" /> Never sent
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex gap-2">
                        <button
                          data-testid={`pending-resend-${u.user_id}`}
                          onClick={() => resend(u)}
                          disabled={!!busy}
                          className="bg-white/10 hover:bg-white/15 disabled:opacity-50 text-white text-xs font-bold px-3 py-1.5 rounded-full inline-flex items-center gap-1.5"
                        >
                          <RefreshCw className={`w-3 h-3 ${busy === "resend" ? "animate-spin" : ""}`} /> Resend
                        </button>
                        <button
                          data-testid={`pending-force-${u.user_id}`}
                          onClick={() => forceVerify(u)}
                          disabled={!!busy}
                          className="bg-emerald-500/25 hover:bg-emerald-500/40 disabled:opacity-50 text-emerald-100 text-xs font-bold px-3 py-1.5 rounded-full inline-flex items-center gap-1.5 border border-emerald-400/40"
                        >
                          <ShieldCheck className="w-3 h-3" /> Force verify
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/**
 * SenderConfigCard — admin can update the Resend From: address + display
 * name without editing backend/.env. Posts to /api/admin/email-sender;
 * the change is persisted to db.system_config and applied to the running
 * server immediately (no restart).
 */
function SenderConfigCard({ delivery, onSaved }) {
  const [email, setEmail] = useState(delivery?.sender_email || "");
  const [name, setName] = useState(delivery?.sender_name || "Tap In TV");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setEmail(delivery?.sender_email || "");
    setName(delivery?.sender_name || "Tap In TV");
  }, [delivery?.sender_email, delivery?.sender_name]);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/email-sender", {
        sender_email: email.trim().toLowerCase(),
        sender_name: name.trim(),
      });
      onSaved?.(data);
      toast.success(data.is_sandbox ? "Saved (still using sandbox)" : "Sender updated — going through your domain now");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't update sender");
    } finally { setBusy(false); }
  };

  const reset = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/email-sender", {
        sender_email: "onboarding@resend.dev",
        sender_name: "Tap In TV",
      });
      onSaved?.(data);
      toast.info("Reverted to Resend sandbox");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't reset");
    } finally { setBusy(false); }
  };

  const isSandbox = delivery?.is_sandbox;
  const wrapClass = isSandbox
    ? "bg-amber-500/15 border-amber-400/40 text-amber-100"
    : "bg-emerald-500/10 border-emerald-500/40 text-emerald-100";

  return (
    <div data-testid="sender-config-card" className={`rounded-xl px-4 py-4 border ${wrapClass} space-y-3`}>
      <div className="flex items-center gap-2 font-bold">
        {isSandbox ? <AlertTriangle className="w-4 h-4 text-amber-300" /> : <CheckCircle2 className="w-4 h-4 text-emerald-300" />}
        {isSandbox ? "Sandbox sender in use — delivery limited to your Resend account owner" : "Production sender configured"}
      </div>
      {isSandbox && (
        <div className="text-xs text-amber-200/80 -mt-1">
          Verify a domain at{" "}
          <a href={delivery?.instructions_url || "https://resend.com/domains"} target="_blank" rel="noreferrer" className="underline font-bold">resend.com/domains</a>
          {" "}then enter your verified address below.
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-[2fr_1.4fr_auto_auto] gap-2 items-end">
        <div>
          <label className="text-[10px] uppercase tracking-wider font-black text-white/60 block mb-1">Sender email</label>
          <input
            data-testid="sender-config-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="noreply@yourdomain.com"
            disabled={busy}
            className="w-full bg-black/40 border border-white/15 focus:border-tv-accent outline-none rounded px-3 py-2 text-white text-sm font-mono"
          />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider font-black text-white/60 block mb-1">Display name</label>
          <input
            data-testid="sender-config-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Tap In TV"
            maxLength={80}
            disabled={busy}
            className="w-full bg-black/40 border border-white/15 focus:border-tv-accent outline-none rounded px-3 py-2 text-white text-sm"
          />
        </div>
        <button
          data-testid="sender-config-save"
          onClick={save}
          disabled={busy || !email.trim()}
          className="bg-tv-accent hover:bg-yellow-400 disabled:opacity-50 text-black font-black text-sm px-4 py-2 rounded h-[38px]"
        >
          {busy ? "Saving…" : "Save"}
        </button>
        {!isSandbox && (
          <button
            data-testid="sender-config-reset"
            onClick={reset}
            disabled={busy}
            title="Revert to onboarding@resend.dev"
            className="bg-white/10 hover:bg-white/20 text-white text-xs font-bold px-3 py-2 rounded h-[38px]"
          >
            Reset
          </button>
        )}
      </div>
      <div className="text-[11px] text-white/50">
        Saving updates the running server immediately — no restart needed. Persisted to <code className="bg-black/30 px-1 rounded">db.system_config</code> so it survives restarts.
      </div>
    </div>
  );
}
